// Corner resize handle — one per rectangle corner (nw, ne, se, sw)
// Encapsulates the resize math: dragging a corner moves the opposite edges

import * as THREE from 'three'
import type { Handle, EditableShape } from '@/types/editor'
import { DPM } from '@/constants/coordinates'

export type CornerPosition = 'nw' | 'ne' | 'se' | 'sw'

const CURSOR_MAP: Record<CornerPosition, string> = {
  nw: 'nwse-resize',
  se: 'nwse-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
}

/** Minimum shape size in editor units (0.1 meter) */
const MIN_SIZE = 5

export class CornerHandle implements Handle {
  readonly type = 'corner' as const
  readonly cursor: string
  readonly mesh: THREE.Mesh
  readonly position: CornerPosition

  private shape: EditableShape
  private dragStartWorld: THREE.Vector3 | null = null
  private startCenter: { x: number; y: number } | null = null
  private startSize: { width: number; height: number } | null = null
  private startRotationRad: number = 0

  constructor(position: CornerPosition, mesh: THREE.Mesh, shape: EditableShape) {
    this.position = position
    this.mesh = mesh
    this.shape = shape
    this.cursor = CURSOR_MAP[position]
  }

  onDragStart(worldPoint: THREE.Vector3): void {
    this.dragStartWorld = worldPoint.clone()
    this.startCenter = this.shape.getCenter()
    this.startSize = this.shape.getSize()
    // Store rotation so we can project world deltas into local frame
    this.startRotationRad = -this.shape.getRotation() * Math.PI / 180
  }

  onDrag(worldPoint: THREE.Vector3): void {
    if (!this.dragStartWorld || !this.startCenter || !this.startSize) return

    // World-space delta in editor units
    const worldDx = (worldPoint.x - this.dragStartWorld.x) * DPM
    const worldDz = (worldPoint.z - this.dragStartWorld.z) * DPM

    // Project into shape's local frame (inverse of Y-rotation)
    const cos = Math.cos(this.startRotationRad)
    const sin = Math.sin(this.startRotationRad)
    const deltaX = worldDx * cos - worldDz * sin
    const deltaZ = worldDx * sin + worldDz * cos

    let newWidth = this.startSize.width
    let newHeight = this.startSize.height

    // Each corner moves two edges. Compute new size.
    switch (this.position) {
      case 'nw':
        newWidth = Math.max(MIN_SIZE, this.startSize.width - deltaX)
        newHeight = Math.max(MIN_SIZE, this.startSize.height - deltaZ)
        break
      case 'ne':
        newWidth = Math.max(MIN_SIZE, this.startSize.width + deltaX)
        newHeight = Math.max(MIN_SIZE, this.startSize.height - deltaZ)
        break
      case 'se':
        newWidth = Math.max(MIN_SIZE, this.startSize.width + deltaX)
        newHeight = Math.max(MIN_SIZE, this.startSize.height + deltaZ)
        break
      case 'sw':
        newWidth = Math.max(MIN_SIZE, this.startSize.width - deltaX)
        newHeight = Math.max(MIN_SIZE, this.startSize.height + deltaZ)
        break
    }

    // Center shift: the size change along each local axis shifts the center
    // by half the delta, but in world space this shift must be rotated back.
    const localDx = (newWidth - this.startSize.width) / 2
    const localDz = (newHeight - this.startSize.height) / 2

    // Flip sign for corners that grow in the negative local direction
    let cx = localDx
    let cz = localDz
    switch (this.position) {
      case 'nw': cx = -cx; cz = -cz; break
      case 'ne': cz = -cz; break
      case 'sw': cx = -cx; break
      // se: both positive, no flip
    }

    // Rotate center shift back to editor coordinates (forward rotation)
    const newX = this.startCenter.x + cx * cos + cz * sin
    const newY = this.startCenter.y - cx * sin + cz * cos

    this.shape.moveTo(newX, newY)
    this.shape.resize(newWidth, newHeight)
  }

  onDragEnd(): void {
    this.dragStartWorld = null
    this.startCenter = null
    this.startSize = null
    this.shape.commit()
  }
}
